import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { PublishedCard } from "@/cards/common/types";
// import {
//   thumbnailRecordToPublishedCard,
// } from "../deckCardThumbnail.types";
// import { useDeckCardThumbnails } from "../useDeckCardThumbnails";
import type { DeckCardType, TalentItem } from "@/cards/common/types";
import {
  mapMainCardResponseToPublishedCard,
  readOwnedDeckCardPurchased,
  type OwnedDeckCardNavigationConfig,
  type OwnedDeckCardPurchaseFilter,
} from "@/cards/common/utils/app-deck/ownedDeckCardList.utils";
import {
  thumbnailRecordToPublishedCard,
  type DeckCardThumbnailRecord,
} from "@/cards/common/types/app-deck/deckCardThumbnail.types";
import { DeckCardKakaoKind } from "@/modules/kakao";
import useBookIconStore from "@/shared/stores/useBookIconStore";
import { useDeckCardThumbnails } from "@/cards/common/hooks/useDeckCardThumbnails";
import { useAuth } from "@/shared/stores/useAuth";
import { useCurrentTalentProfile } from "@/shared";
import { useGetCardById } from "@/cards/common/api/taascard.api";

export type OwnedDeckCardTitleFormat = "possessive" | "bullet";

export type UseOwnedDeckCardListOptions = {
  deckCardType: DeckCardType;
  /** User purchased list vs talent-owned list. */
  viewMode: "user" | "talent";
  purchaseFilter?: OwnedDeckCardPurchaseFilter;
  navigation: OwnedDeckCardNavigationConfig;
  /** Preserved in URL when setting/removing `id` (e.g. single_tab=purchased). */
  tabParamKey?: string;
  tabParamValue?: string;
  thumbnailExtraParams?: Record<string, string>;
  titleLabel?: string;
  titleFormat?: OwnedDeckCardTitleFormat;
  enabled?: boolean;
  appendEndingCoverOnFetch?: boolean;
  /** Override main-card → PublishedCard mapping after book-view fetch. */
  mapFetchedMainCard?: (
    rawCard: Record<string, unknown>,
    options?: {
      titleLabel?: string;
      appendEndingCover?: boolean;
      isTalentView?: boolean;
    }
  ) => PublishedCard;
  enrichThumbnailCard?: (card: PublishedCard, record: DeckCardThumbnailRecord) => PublishedCard;
  /** Secondary URL param synced when opening book preview (e.g. demoCardId). */
  secondaryUrlParam?: {
    key: string;
    getValue: (card: PublishedCard) => string | undefined;
  };
  /** Extra search params removed on preview/expand close (e.g. step). */
  extraUrlParamsToClear?: string[];
  /** Resolve deep links from URL params (supports demoCardId-only links). */
  resolveDeepLinkCard?: (
    cards: PublishedCard[],
    searchParams: URLSearchParams
  ) => PublishedCard | null;
  /** Extra filter after purchase filter (e.g. talent-owned cards only). */
  filterVisibleCard?: (card: PublishedCard) => boolean;
  onAfterPreviewClose?: () => void;
  refetchOnPreviewClose?: boolean;
  /** Talent share from deck expand back face (e.g. season card Kakao). */
  onExpandShare?: (card: PublishedCard) => void;
  deckCardShareKind?: DeckCardKakaoKind;
};

export function useOwnedDeckCardList({
  deckCardType,
  viewMode,
  purchaseFilter = "all",
  navigation,
  tabParamKey,
  tabParamValue,
  thumbnailExtraParams,
  titleLabel,
  enabled = true,
  appendEndingCoverOnFetch = true,
  titleFormat = "possessive",
  mapFetchedMainCard = mapMainCardResponseToPublishedCard,
  enrichThumbnailCard,
  secondaryUrlParam,
  extraUrlParamsToClear = [],
  resolveDeepLinkCard,
  filterVisibleCard,
  onAfterPreviewClose,
  refetchOnPreviewClose = false,
  onExpandShare,
  deckCardShareKind,
}: UseOwnedDeckCardListOptions) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { userInfo } = useAuth();
  const { currentProfile } = useCurrentTalentProfile();
  const { isBookIconSelected, isCollageViewSelected } = useBookIconStore();

  const isTalentView = viewMode === "talent";
  const resolvedTalentId = isTalentView ? (currentProfile?.id ?? null) : null;

  const resolvedThumbnailExtraParams = useMemo(() => {
    const base: Record<string, string> = { ...(thumbnailExtraParams ?? {}) };
    if (viewMode === "user" && purchaseFilter === "purchased") {
      base.purchased = "true";
    } else if (viewMode === "user" && purchaseFilter === "available") {
      base.purchased = "false";
    }
    return Object.keys(base).length > 0 ? base : undefined;
  }, [thumbnailExtraParams, viewMode, purchaseFilter]);

  const {
    thumbnailsByCardId,
    thumbnailRecords,
    isLoading: thumbnailsLoading,
    refetch: refetchThumbnails,
  } = useDeckCardThumbnails({
    type: deckCardType,
    enabled: enabled && Boolean(userInfo?.id),
    talentId: resolvedTalentId,
    extraParams: resolvedThumbnailExtraParams,
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTalentId, setSelectedTalentId] = useState<number | string | null>(null);

  const [expandCard, setExpandCard] = useState<PublishedCard | null>(null);
  const [isExpandOpen, setIsExpandOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState<PublishedCard | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [cardDetailsFetchId, setCardDetailsFetchId] = useState<number | null>(null);

  const lastProcessedIdRef = useRef<string | null>(null);

  const transformedCards: PublishedCard[] = useMemo(() => {
    return thumbnailRecords.map((record) => {
      let card = thumbnailRecordToPublishedCard(record);
      const label = titleLabel ?? t("single_card", { ns: "taascard", defaultValue: "Single Card" });
      if (record.talent?.username) {
        card.title =
          titleFormat === "bullet"
            ? `${record.talent.username} • ${label}`
            : `${record.talent.username}'s ${label}`;
      }
      if (enrichThumbnailCard) {
        card = enrichThumbnailCard(card, record);
      }
      // card-thumbnail?purchased=true already filtered — stamp is_purchased for preview/expand.
      if (viewMode === "user" && purchaseFilter === "purchased") {
        const pages = Array.isArray(card.pages) ? [...card.pages] : [];
        if (pages[0]) {
          pages[0] = {
            ...pages[0],
            content: { ...(pages[0].content ?? {}), is_purchased: true },
          };
        }
        card = { ...card, pages };
      }
      return card;
    });
  }, [
    thumbnailRecords,
    t,
    titleLabel,
    titleFormat,
    enrichThumbnailCard,
    viewMode,
    purchaseFilter,
    deckCardType,
  ]);

  // When the API is called with purchased=true|false, trust that response — do not
  // re-filter on local purchase flags (those are often missing on thumbnails).
  const apiFiltersByPurchase =
    viewMode === "user" && (purchaseFilter === "purchased" || purchaseFilter === "available");

  const visibleCards = useMemo(() => {
    let items =
      apiFiltersByPurchase || purchaseFilter === "all"
        ? transformedCards
        : transformedCards.filter((card) => {
            const record = thumbnailsByCardId.get(card.id);
            const purchased = readOwnedDeckCardPurchased(card, record, deckCardType);
            return purchaseFilter === "purchased" ? purchased : !purchased;
          });
    if (filterVisibleCard) {
      items = items.filter(filterVisibleCard);
    }
    return items;
  }, [
    transformedCards,
    purchaseFilter,
    thumbnailsByCardId,
    deckCardType,
    filterVisibleCard,
    apiFiltersByPurchase,
  ]);

  const uniqueTalents = useMemo((): TalentItem[] => {
    const seen = new Set<string>();
    const list: TalentItem[] = [];
    visibleCards.forEach((card) => {
      const talent = card.talent;
      if (!talent?.id) return;
      const id = String(talent.id);
      if (seen.has(id)) return;
      seen.add(id);
      list.push({
        id: talent.id,
        username: talent.username ?? talent.name ?? id,
        profile_image: talent.profile_image ?? "",
      });
    });
    return list;
  }, [visibleCards]);

  const filteredCards = useMemo(() => {
    let items = visibleCards;

    if (selectedTalentId !== null) {
      items = items.filter(
        (card) => card.talent && String(card.talent.id) === String(selectedTalentId)
      );
    }

    const q = searchQuery.trim().toLowerCase();
    if (!q) return items;

    return items.filter((card) => {
      const talent = card.talent;
      const username = (talent?.username ?? talent?.name ?? "").toLowerCase();
      const title = (card.title ?? "").toLowerCase();
      const primary = card.pages?.[0]?.content ?? {};
      const desc = String(primary?.description ?? "").toLowerCase();
      return username.includes(q) || title.includes(q) || desc.includes(q);
    });
  }, [visibleCards, selectedTalentId, searchQuery]);

  const syncSecondaryUrlParam = useCallback(
    (card: PublishedCard | null) => {
      if (!secondaryUrlParam) return;
      const sp = new URLSearchParams(location.search);
      if (tabParamKey && tabParamValue) {
        sp.set(tabParamKey, tabParamValue);
      }
      if (card) {
        const value = secondaryUrlParam.getValue(card);
        if (value) sp.set(secondaryUrlParam.key, value);
        else sp.delete(secondaryUrlParam.key);
      } else {
        sp.delete(secondaryUrlParam.key);
      }
      navigate({ pathname: location.pathname, search: sp.toString() }, { replace: true });
    },
    [secondaryUrlParam, location.pathname, location.search, navigate, tabParamKey, tabParamValue]
  );

  const setIdInUrl = useCallback(
    (id: string) => {
      const sp = new URLSearchParams(location.search);
      if (tabParamKey && tabParamValue) {
        sp.set(tabParamKey, tabParamValue);
      }
      sp.set("id", id);
      navigate({ pathname: location.pathname, search: sp.toString() }, { replace: true });
    },
    [location.pathname, location.search, navigate, tabParamKey, tabParamValue]
  );

  const removeIdFromUrl = useCallback(() => {
    const sp = new URLSearchParams(location.search);
    if (tabParamKey && tabParamValue) {
      sp.set(tabParamKey, tabParamValue);
    }
    sp.delete("id");
    extraUrlParamsToClear.forEach((key) => sp.delete(key));
    if (secondaryUrlParam) {
      sp.delete(secondaryUrlParam.key);
    }
    navigate({ pathname: location.pathname, search: sp.toString() }, { replace: true });
  }, [
    location.pathname,
    location.search,
    navigate,
    tabParamKey,
    tabParamValue,
    extraUrlParamsToClear,
    secondaryUrlParam,
  ]);

  const beginCardDetailsFetch = useCallback((card: PublishedCard) => {
    const id = Number(card.id);
    if (Number.isFinite(id) && id > 0) {
      setCardDetailsFetchId(id);
    }
  }, []);

  const clearCardDetailsFetch = useCallback(() => {
    setCardDetailsFetchId(null);
  }, []);

  const { data: detailsCardResponse, refetch: refetchCardById } = useGetCardById({
    key: ["main-card-by-id", cardDetailsFetchId, viewMode],
    params: {
      id: Number(cardDetailsFetchId ?? 0),
      ...(currentProfile?.id && {
        headers: { "talent-id": currentProfile.id.toString() },
      }),
    },
    options: {
      enabled: Boolean(cardDetailsFetchId),
      refetchOnMount: false,
      staleTime: isPreviewOpen && isTalentView ? 0 : 60_000,
    },
  });

  useEffect(() => {
    if (!isPreviewOpen) {
      clearCardDetailsFetch();
    }
  }, [isPreviewOpen, clearCardDetailsFetch]);

  useEffect(() => {
    const rawCard = detailsCardResponse?.data as Record<string, unknown> | undefined;
    if (!rawCard || !isPreviewOpen || !selectedCard) return;
    if (String(rawCard.id) !== selectedCard.id) return;

    const label = titleLabel ?? t("single_card", { ns: "taascard", defaultValue: "Single Card" });
    const refreshed = mapFetchedMainCard(rawCard, {
      titleLabel: label,
      appendEndingCover: appendEndingCoverOnFetch,
      isTalentView,
    });
    setSelectedCard(refreshed);
  }, [
    detailsCardResponse,
    isPreviewOpen,
    selectedCard?.id,
    t,
    titleLabel,
    appendEndingCoverOnFetch,
    mapFetchedMainCard,
    isTalentView,
  ]);

  const openCardExpand = useCallback(
    (card: PublishedCard) => {
      lastProcessedIdRef.current = String(card.id);
      setExpandCard(card);
      setIsExpandOpen(true);
      setIdInUrl(card.id);
    },
    [setIdInUrl]
  );

  const closeCardExpand = useCallback(() => {
    if (expandCard?.id) {
      lastProcessedIdRef.current = String(expandCard.id);
    }
    setIsExpandOpen(false);
    setExpandCard(null);
    removeIdFromUrl();
  }, [removeIdFromUrl, expandCard?.id]);

  const dismissExpandForDetails = useCallback(() => {
    setIsExpandOpen(false);
    setExpandCard(null);
  }, []);

  const openCardDetails = useCallback(
    (card: PublishedCard) => {
      if (isCollageViewSelected && navigation.getCollagePath) {
        navigate(navigation.getCollagePath(card, isTalentView), {
          state: { card },
        });
        return;
      }

      if (isBookIconSelected) {
        beginCardDetailsFetch(card);
        setSelectedCard(card);
        setIsPreviewOpen(true);
        setIdInUrl(card.id);
        syncSecondaryUrlParam(card);
        return;
      }

      navigate(navigation.getListPath(card, isTalentView), { state: { card } });
    },
    [
      isCollageViewSelected,
      isBookIconSelected,
      navigate,
      navigation,
      isTalentView,
      beginCardDetailsFetch,
      setIdInUrl,
      syncSecondaryUrlParam,
    ]
  );

  const handleCardClick = useCallback(
    (card: PublishedCard) => {
      openCardExpand(card);
    },
    [openCardExpand]
  );

  const handlePreviewClose = useCallback(() => {
    setIsPreviewOpen(false);
    setSelectedCard(null);
    removeIdFromUrl();
    if (refetchOnPreviewClose) {
      void refetchThumbnails();
    }
    onAfterPreviewClose?.();
  }, [removeIdFromUrl, onAfterPreviewClose, refetchOnPreviewClose, refetchThumbnails]);

  const refetchSelectedCard = useCallback(() => {
    if (cardDetailsFetchId) {
      void refetchCardById();
      return;
    }
    if (selectedCard) {
      beginCardDetailsFetch(selectedCard);
    }
  }, [cardDetailsFetchId, refetchCardById, selectedCard, beginCardDetailsFetch]);

  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const idFromUrl = sp.get("id");
    const deepLinkCard = resolveDeepLinkCard
      ? resolveDeepLinkCard(visibleCards, sp)
      : idFromUrl
        ? (visibleCards.find((c) => String(c.id) === idFromUrl) ?? null)
        : null;

    if (!deepLinkCard && !idFromUrl) {
      lastProcessedIdRef.current = null;
      return;
    }
    if (isPreviewOpen) return;
    if (
      isExpandOpen &&
      expandCard &&
      String(expandCard.id) === String(deepLinkCard?.id ?? idFromUrl)
    ) {
      lastProcessedIdRef.current = String(deepLinkCard?.id ?? idFromUrl);
      return;
    }

    const resolvedId = String(deepLinkCard?.id ?? idFromUrl ?? "");
    if (!resolvedId || lastProcessedIdRef.current === resolvedId) return;

    const card = deepLinkCard ?? visibleCards.find((c) => String(c.id) === resolvedId) ?? null;
    if (!card) {
      if (thumbnailsLoading) return;
      return;
    }

    lastProcessedIdRef.current = resolvedId;
    openCardExpand(card);
  }, [
    visibleCards,
    thumbnailsLoading,
    location.search,
    openCardExpand,
    isPreviewOpen,
    isExpandOpen,
    expandCard,
    resolveDeepLinkCard,
  ]);

  useEffect(() => {
    if (expandCard && visibleCards.length > 0) {
      const updated = visibleCards.find((c) => c.id === expandCard.id);
      if (updated) setExpandCard(updated);
    }
  }, [visibleCards, expandCard?.id]);

  const expandPortalProps = {
    open: isExpandOpen,
    onClose: closeCardExpand,
    onDismiss: dismissExpandForDetails,
    card: expandCard,
    thumbnail: expandCard ? (thumbnailsByCardId.get(expandCard.id) ?? null) : null,
    onOpenDetails: openCardDetails,
    onShare: onExpandShare,
    deckCardShareKind,
  };

  return {
    loading: thumbnailsLoading,
    filteredCards,
    uniqueTalents,
    thumbnailsByCardId,
    searchQuery,
    setSearchQuery,
    selectedTalentId,
    setSelectedTalentId,
    selectedCard,
    isPreviewOpen,
    handleCardClick,
    handlePreviewClose,
    expandPortalProps,
    deckCardType,
    isTalentView,
    refetchThumbnails,
    refetchSelectedCard,
  };
}
